<script setup lang="ts">
import { reactive } from 'vue';
import { getVolume, setVolume, setIsMuted, isMuted } from '@/sound-utils';
import SoundOff from '@/components/icons/SoundOff.vue';
import SoundOn from '@/components/icons/SoundOn.vue';

const state = reactive({
  isMuted: isMuted(),
  volume: getVolume(),
});

function handleVolumeChange(e: Event) {
  if (!e.target) return;
  const vol = e.target as HTMLInputElement;
  state.volume = Number.parseFloat(vol.value);
  setVolume(state.volume);
}

const toggleSound = () => {
  state.isMuted = !state.isMuted;
  setIsMuted(state.isMuted);
};

setVolume(state.volume);
setIsMuted(state.isMuted);
</script>

<template>
  <div class="container">
    <div class="sound-button">
      <button
        id="sound"
        :class="{ soundOn: state.isMuted, soundOff: !state.isMuted }"
        @click="toggleSound"
      >
        <SoundOn class="no-pointer" color="var(--color-light-orange)" v-if="!state.isMuted" />
        <SoundOff class="no-pointer" color="var(--color-light-orange)" v-else />
      </button>
    </div>
    <input
      :value="state.volume"
      @input="handleVolumeChange"
      min="0"
      max="1"
      step="0.01"
      type="range"
      :disabled="state.isMuted"
      class="volume-meter"
    />
  </div>
</template>

<style scoped>
.text-shadow {
  text-shadow: 0 2px 4px rgba(0, 0, 0, 75%);
}

.container {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.soundOn {
  background: var(--color-orange);
  color: var(--color-light-orange);
}

.soundOff {
  background: var(--color-blue);
  color: var(--color-light-orange);
}

.volume-meter {
  height: 1.5rem;
  width: 7rem;
  transition: opacity 0.1s;
  background: rgba(0, 0, 0, 0);
  cursor: pointer;
}

.volume-meter:disabled {
  opacity: 0.5;
  cursor: auto;
}

.volume-meter::-moz-range-track {
  background: #ccc;
}
.volume-meter::-moz-range-progress {
  background: var(--color-blue);
}
.volume-meter:disabled::-moz-range-progress {
  background: var(--color-orange);
}

.no-pointer {
  pointer-events: none;
}

#sound {
  font-weight: 400;
  text-transform: uppercase;
  border-radius: 7px;
  font-size: 14px;
  width: 32px;
  height: 32px;
  transition: transform 150ms ease-out;
}

#sound:hover {
  transform: scale(1.05);
}

#sound:active {
  transform: scale(0.95);
}
</style>
